import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isLibraryUrl } from '@/lib/photoLibraryTypes';

/**
 * Extracts the storage object path from a photo URL. Handles both full
 * Supabase public URLs (containing `/object/public/{bucket}/`) and plain
 * storage paths.
 *
 * @param photoUrl - Full URL or plain storage path.
 * @param bucket - The Supabase storage bucket name.
 * @returns The storage path portion suitable for `createSignedUrl`.
 */
function extractPath(photoUrl: string, bucket: string): string {
  const marker = `/object/public/${bucket}/`;
  const idx = photoUrl.indexOf(marker);
  if (idx !== -1) {
    return photoUrl.substring(idx + marker.length);
  }
  return photoUrl;
}

/**
 * Returns a time-limited signed URL for a private Supabase storage object.
 *
 * Extracts the storage path from `photoUrl` (handles both full public URLs
 * and plain paths), then calls `createSignedUrl`. Results are cached for the
 * lifetime of the component. A cleanup flag prevents stale state updates
 * after unmount or when `photoUrl` changes before the request resolves.
 *
 * @param photoUrl - Full Supabase URL or plain storage path. `null`/`undefined` returns `null`.
 * @param bucket - Storage bucket name (defaults to `'item-photos'`).
 * @param expiresIn - Signed URL validity in seconds (defaults to `3600`).
 * @returns `{ url, isError }` — `url` is `null` while loading or on error.
 */
export function useSignedUrl(
  photoUrl: string | null | undefined,
  bucket = 'item-photos',
  expiresIn = 3600,
): { url: string | null; isError: boolean } {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!photoUrl) {
      setSignedUrl(null);
      setIsError(false);
      return;
    }

    // Library photos are in a public bucket — return the URL directly without signing.
    if (isLibraryUrl(photoUrl)) {
      setSignedUrl(photoUrl);
      setIsError(false);
      return;
    }

    // Clear stale URL immediately when starting a new request.
    setSignedUrl(null);
    setIsError(false);

    // [LEARN] Stale closure prevention: if the component unmounts or photoUrl changes before the
    // async fetch completes, this flag stops us from calling setState on an unmounted component.
    let cancelled = false;
    const path = extractPath(photoUrl, bucket);

    supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn)
      .then(({ data, error }) => {
        if (cancelled) return; // Discard result if the effect was superseded
        if (!error && data) {
          setSignedUrl(data.signedUrl);
        } else {
          // Keep null so callers never render a stale or wrong URL.
          setSignedUrl(null);
          setIsError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [photoUrl, bucket, expiresIn]);

  return { url: signedUrl, isError };
}
